import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { Persona, PersonaCreateSchema, PersonaUpdateSchema } from "../types";
import { type ApiMutationOptions, useApiMutation, type WithDynamicUrl } from "./use-api-mutation";
import { useApiQuery } from "./use-api-query";

interface PersonasQueryParams {
  q?: string;
  app_config_id?: string;
}

export const usePersonas = (params?: PersonasQueryParams) => {
  const queryString = new URLSearchParams();

  if (params?.q) {
    queryString.append("q", params.q);
  }
  if (params?.app_config_id) {
    queryString.append("app_config_id", params.app_config_id);
  }

  const query = queryString.toString() ? `/personas?${queryString.toString()}` : "/personas";

  return useApiQuery<Persona[]>(query);
};

export const usePersona = (id: string) => {
  return useApiQuery<Persona>(`/personas/${id}`, {
    enabled: !!id,
  });
};

export const useCreatePersona = (opts?: ApiMutationOptions<PersonaCreateSchema, Persona>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useApiMutation<PersonaCreateSchema, Persona>("POST", "/personas", {
    ...opts,
    onSuccess: (persona, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["/personas"] });

      enqueueSnackbar(`Persona '${persona.name}' created successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onSuccess?.(persona, variables, context);
    },
    onError: (error, variables, context) => {
      enqueueSnackbar(`Failed to create persona: ${error}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onError?.(error, variables, context);
    },
  });
};

export const useUpdatePersona = (
  opts?: ApiMutationOptions<WithDynamicUrl<PersonaUpdateSchema>, Persona>,
) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useApiMutation<WithDynamicUrl<PersonaUpdateSchema>, Persona>("PATCH", "", {
    ...opts,
    onSuccess: (persona, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [`/personas/${persona.id}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/personas"] });

      enqueueSnackbar(`Persona '${persona.name}' updated successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onSuccess?.(persona, variables, context);
    },
    onError: (error, variables, context) => {
      enqueueSnackbar(`Failed to update persona: ${error}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onError?.(error, variables, context);
    },
  });
};

export const useDeletePersona = (opts?: ApiMutationOptions<WithDynamicUrl<void>, void>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useApiMutation<WithDynamicUrl<void>, void>("DELETE", "", {
    ...opts,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["/personas"] });

      enqueueSnackbar(`Persona deleted successfully`, {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onSuccess?.(data, vars, ctx);
    },
    onError: (error, vars, ctx) => {
      enqueueSnackbar(`Failed to delete persona: ${error}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      opts?.onError?.(error, vars, ctx);
    },
  });
};
